import React, { useEffect, useState } from 'react'
import { SvgIcon, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  formatGravityDateValue,
  getDayjsDateFormatFromGravity,
  parseGravityDateValue
} from '@/utils/form/date-format'
import { WPCF7Form } from '@/types'

export type DateFieldProps = {
  field: WPCF7Form['form'][0]
  formik: any
}

const MIN_AGE = 16

const CalendarPickerIcon = () => (
  <SvgIcon fontSize="small">
    <use href="#ico-calendar" />
  </SvgIcon>
)

const DateField = ({ field, formik }: DateFieldProps) => {
  const [isMounted, setIsMounted] = useState(false)
  const isBirthDate = field.adminLabel === 'date_of_birth'
  const maxDate = isBirthDate ? dayjs().subtract(MIN_AGE, 'year') : undefined
  const fieldDateFormat = getDayjsDateFormatFromGravity(field.dateFormat)
  const shouldShowCalendarIcon = field.calendarIconType === 'calendar'

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <>
      {field.label && field.labelPlacement !== 'hidden_label' && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 1,
            ...(formik.submitCount > 0 &&
              formik.errors[`input_${field.id}`] !== undefined &&
              formik.touched[`input_${field.id}`] !== undefined && {
                color: 'error.main'
              })
          }}
        >
          {field.label}
          {field.isRequired ? ' *' : ''}
        </Typography>
      )}
      {isMounted ? (
        <DatePicker
          label=""
          format={fieldDateFormat}
          maxDate={maxDate}
          value={parseGravityDateValue(formik.values[`input_${field.id}`], field.dateFormat)}
          onChange={(newValue) => {
            formik.setFieldValue(
              `input_${field.id}`,
              newValue ? formatGravityDateValue(newValue, field.dateFormat) : ''
            )
          }}
          slots={shouldShowCalendarIcon ? { openPickerIcon: CalendarPickerIcon } : undefined}
          sx={{
            '& .MuiPickersInputBase-root': {
              fontSize: '1.6rem',
              lineHeight: '160%'
            },
            '& .MuiPickersInputBase-sectionsContainer': {
              padding: '0px 0 5px'
            }
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'standard',
              error:
                formik.submitCount > 0 &&
                formik.errors[`input_${field.id}`] !== undefined &&
                formik.touched[`input_${field.id}`] !== undefined,
              helperText:
                formik.submitCount > 0 &&
                formik.touched[`input_${field.id}`] &&
                formik.errors[`input_${field.id}`]
                  ? String(formik.errors[`input_${field.id}`])
                  : undefined,
              onBlur: () => {
                formik.handleBlur({ target: { name: `input_${field.id}` } })
              }
            },
            openPickerButton: shouldShowCalendarIcon
              ? {
                  color: 'default',
                  sx: {
                    p: 0,
                    mb: 0.5
                  }
                }
              : undefined,
            openPickerIcon: shouldShowCalendarIcon
              ? {
                  sx: {
                    fontSize: 16
                  }
                }
              : undefined
          }}
        />
      ) : (
        <TextField fullWidth variant="standard" disabled />
      )}
    </>
  )
}

export default DateField
